/**
 * POS Offline Policies
 * 
 * Central policy helper for POS-specific offline actions.
 * Defines which POS operations can be performed offline and how they should be handled.
 */

import { ACTION_CATEGORIES, POLICY_MODES, SEVERITY } from './backofficePolicies';

/**
 * POS-specific action types
 */
export const POS_ACTION_TYPES = {
  // Order operations
  ORDER_CREATE: 'POS_ORDER_CREATE',
  ORDER_UPDATE: 'POS_ORDER_UPDATE',
  ORDER_ITEM_ADD: 'POS_ORDER_ITEM_ADD',
  ORDER_ITEM_REMOVE: 'POS_ORDER_ITEM_REMOVE',
  ORDER_ITEM_MODIFY: 'POS_ORDER_ITEM_MODIFY',
  ORDER_DISCOUNT: 'POS_ORDER_DISCOUNT',
  ORDER_VOID: 'POS_ORDER_VOID',
  ORDER_CLOSE: 'POS_ORDER_CLOSE',
  
  // Payment operations  
  PAYMENT_CASH: 'POS_PAYMENT_CASH',
  PAYMENT_CARD: 'POS_PAYMENT_CARD',
  PAYMENT_SPLIT: 'POS_PAYMENT_SPLIT',
  PAYMENT_TIP: 'POS_PAYMENT_TIP',
  
  // Table operations
  TABLE_ASSIGN: 'POS_TABLE_ASSIGN',
  TABLE_TRANSFER: 'POS_TABLE_TRANSFER',
  TABLE_MERGE: 'POS_TABLE_MERGE',
  
  // Shift operations
  SHIFT_OPEN: 'POS_SHIFT_OPEN',
  SHIFT_CLOSE: 'POS_SHIFT_CLOSE',
  CASH_DROP: 'POS_CASH_DROP',
  CASH_PAYOUT: 'POS_CASH_PAYOUT',
  
  // Kitchen/Printer
  PRINT_TICKET: 'POS_PRINT_TICKET',
  KITCHEN_SEND: 'POS_KITCHEN_SEND',
  KITCHEN_BUMP: 'POS_KITCHEN_BUMP'
};

/**
 * Offline policies for POS action types
 */
const POS_POLICIES = {
  // ==================== SAFE ACTIONS ====================
  [POS_ACTION_TYPES.ORDER_CREATE]: {
    category: ACTION_CATEGORIES.SAFE,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.INFO,
    message: 'Order created offline - will sync when online',
    description: 'Create new order with items',
    dedupe: false, // Each order is unique
    entityType: 'pos_order',
    priority: 1
  },
  
  [POS_ACTION_TYPES.ORDER_UPDATE]: {
    category: ACTION_CATEGORIES.SAFE,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.INFO,
    message: 'Order updated offline',
    description: 'Update order details (customer name, type)',
    dedupe: true, // Multiple updates to same order can collapse
    entityType: 'pos_order',
    priority: 2
  },
  
  [POS_ACTION_TYPES.ORDER_ITEM_ADD]: {
    category: ACTION_CATEGORIES.SAFE,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.INFO,
    message: 'Item added offline',
    description: 'Add item to order',
    dedupe: false,
    entityType: 'pos_order_item',
    priority: 2
  },
  
  [POS_ACTION_TYPES.ORDER_ITEM_REMOVE]: {
    category: ACTION_CATEGORIES.SAFE,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.INFO,
    message: 'Item removed offline',
    description: 'Remove item from order',
    dedupe: false,
    entityType: 'pos_order_item',
    priority: 2
  },
  
  [POS_ACTION_TYPES.ORDER_ITEM_MODIFY]: {
    category: ACTION_CATEGORIES.SAFE,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.INFO,
    message: 'Item modified offline',
    description: 'Modify item quantity or modifiers',
    dedupe: true,
    entityType: 'pos_order_item',
    priority: 2
  },
  
  [POS_ACTION_TYPES.ORDER_DISCOUNT]: {
    category: ACTION_CATEGORIES.CAUTION,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.WARNING,
    message: 'Discount applied offline - will be reviewed',
    description: 'Apply discount to order',
    dedupe: true,
    entityType: 'pos_order',
    requiresReview: true,
    priority: 2
  },
  
  [POS_ACTION_TYPES.TABLE_ASSIGN]: {
    category: ACTION_CATEGORIES.SAFE,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.INFO,
    message: 'Table assigned offline',
    description: 'Assign order to table',
    dedupe: true,
    entityType: 'pos_table',
    priority: 3
  },
  
  [POS_ACTION_TYPES.TABLE_TRANSFER]: {
    category: ACTION_CATEGORIES.SAFE,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.INFO,
    message: 'Table transfer queued',
    description: 'Transfer order to different table',
    dedupe: true,
    entityType: 'pos_table',
    priority: 3
  },
  
  [POS_ACTION_TYPES.PRINT_TICKET]: {
    category: ACTION_CATEGORIES.SAFE,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.INFO,
    message: 'Print job queued',
    description: 'Print receipt or ticket',
    dedupe: false,
    entityType: 'pos_print',
    priority: 4
  },
  
  [POS_ACTION_TYPES.KITCHEN_SEND]: {
    category: ACTION_CATEGORIES.SAFE,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.INFO,
    message: 'Kitchen ticket queued',
    description: 'Send order to kitchen',
    dedupe: false,
    entityType: 'pos_kitchen',
    priority: 1 // High priority
  },
  
  [POS_ACTION_TYPES.KITCHEN_BUMP]: {
    category: ACTION_CATEGORIES.SAFE,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.INFO,
    message: 'Kitchen bump queued',
    description: 'Mark order as ready',
    dedupe: false,
    entityType: 'pos_kitchen',
    priority: 2
  },
  
  // ==================== CAUTION ACTIONS ====================
  [POS_ACTION_TYPES.PAYMENT_CASH]: {
    category: ACTION_CATEGORIES.CAUTION,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.WARNING,
    message: 'Cash payment recorded offline',
    description: 'Record cash payment',
    dedupe: false,
    entityType: 'pos_payment',
    requiresReview: true,
    priority: 1
  },
  
  [POS_ACTION_TYPES.PAYMENT_TIP]: {
    category: ACTION_CATEGORIES.CAUTION,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.WARNING,
    message: 'Tip recorded offline',
    description: 'Add tip to payment',
    dedupe: true,
    entityType: 'pos_payment',
    requiresReview: true,
    priority: 2
  },
  
  [POS_ACTION_TYPES.ORDER_CLOSE]: {
    category: ACTION_CATEGORIES.CAUTION,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.WARNING,
    message: 'Order closure queued for sync',
    description: 'Close order after payment',
    dedupe: false,
    entityType: 'pos_order',
    requiresReview: true,
    priority: 1
  },
  
  [POS_ACTION_TYPES.SHIFT_OPEN]: {
    category: ACTION_CATEGORIES.CAUTION,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.WARNING,
    message: 'Shift opened offline',
    description: 'Open new shift/drawer',
    dedupe: false,
    entityType: 'pos_shift',
    requiresReview: true,
    priority: 1
  },
  
  [POS_ACTION_TYPES.SHIFT_CLOSE]: {
    category: ACTION_CATEGORIES.CAUTION,
    mode: POLICY_MODES.MANUAL_REVIEW_ONLY,
    severity: SEVERITY.WARNING,
    message: 'Shift closure requires manager review',
    description: 'Close shift with cash count',
    dedupe: false,
    entityType: 'pos_shift',
    requiresReview: true,
    priority: 1
  },
  
  [POS_ACTION_TYPES.CASH_DROP]: {
    category: ACTION_CATEGORIES.CAUTION,
    mode: POLICY_MODES.QUEUE,
    severity: SEVERITY.WARNING,
    message: 'Cash drop recorded offline',
    description: 'Record cash drop to safe',
    dedupe: false,
    entityType: 'pos_cash',
    requiresReview: true,
    priority: 2
  },
  
  // ==================== BLOCKED ACTIONS ====================
  [POS_ACTION_TYPES.PAYMENT_CARD]: {
    category: ACTION_CATEGORIES.BLOCKED,
    mode: POLICY_MODES.BLOCK,
    severity: SEVERITY.ERROR,
    message: 'Card payments require active connection',
    description: 'Process card payment through terminal',
    dedupe: false,
    entityType: 'pos_payment'
  },
  
  [POS_ACTION_TYPES.PAYMENT_SPLIT]: {
    category: ACTION_CATEGORIES.BLOCKED,
    mode: POLICY_MODES.BLOCK,
    severity: SEVERITY.ERROR,
    message: 'Split payments require active connection',
    description: 'Split payment across multiple methods',
    dedupe: false,
    entityType: 'pos_payment'
  },
  
  [POS_ACTION_TYPES.ORDER_VOID]: {
    category: ACTION_CATEGORIES.BLOCKED,
    mode: POLICY_MODES.BLOCK,
    severity: SEVERITY.ERROR,
    message: 'Order voids require manager approval online',
    description: 'Void order with refund',
    dedupe: false,
    entityType: 'pos_order'
  },
  
  [POS_ACTION_TYPES.TABLE_MERGE]: {
    category: ACTION_CATEGORIES.BLOCKED,
    mode: POLICY_MODES.BLOCK,
    severity: SEVERITY.ERROR,
    message: 'Table merge requires active connection',
    description: 'Merge multiple tables/orders',
    dedupe: false,
    entityType: 'pos_table'
  },
  
  [POS_ACTION_TYPES.CASH_PAYOUT]: {
    category: ACTION_CATEGORIES.BLOCKED,
    mode: POLICY_MODES.BLOCK,
    severity: SEVERITY.ERROR,
    message: 'Cash payouts require manager approval online',
    description: 'Cash payout from drawer',
    dedupe: false,
    entityType: 'pos_cash'
  }
};

/**
 * Check if a POS action is allowed offline
 * @param {string} actionType - The action type to check
 * @returns {boolean}
 */
export function isPOSActionOfflineAllowed(actionType) {
  const policy = POS_POLICIES[actionType];
  if (!policy) return false;
  return policy.mode !== POLICY_MODES.BLOCK;
}

/**
 * Get the offline policy for a POS action
 * @param {string} actionType - The action type
 * @returns {Object} Policy object
 */
export function getPOSOfflinePolicy(actionType) {
  const policy = POS_POLICIES[actionType];
  
  if (!policy) {
    return {
      allowed: false,
      mode: POLICY_MODES.BLOCK,
      reason: 'Unknown POS action type',
      severity: SEVERITY.ERROR,
      message: 'This action is not configured for offline support',
      dedupe: false,
      entityType: 'unknown'
    };
  }
  
  return {
    allowed: policy.mode !== POLICY_MODES.BLOCK,
    mode: policy.mode,
    reason: policy.description,
    severity: policy.severity,
    message: policy.message,
    dedupe: policy.dedupe,
    entityType: policy.entityType,
    requiresReview: policy.requiresReview || false,
    category: policy.category,
    priority: policy.priority || 5
  };
}

/**
 * Get user-friendly message for POS offline action
 * @param {string} actionType 
 * @returns {Object}
 */
export function getPOSOfflineMessage(actionType) {
  const policy = POS_POLICIES[actionType];
  
  if (!policy) {
    return {
      title: 'Action Not Available',
      description: 'This action requires an active connection.',
      severity: SEVERITY.ERROR,
      canRetry: false
    };
  }
  
  if (policy.mode === POLICY_MODES.BLOCK) {
    return {
      title: 'Connection Required',
      description: policy.message,
      severity: policy.severity,
      canRetry: false
    };
  }
  
  if (policy.requiresReview) {
    return {
      title: 'Saved for Review',
      description: `${policy.message}. A manager will review this action.`,
      severity: policy.severity,
      canRetry: true,
      requiresReview: true
    };
  }
  
  return {
    title: 'Saved Offline',
    description: policy.message,
    severity: policy.severity,
    canRetry: true
  };
}

/**
 * Get display name for POS action type
 * @param {string} actionType 
 * @returns {string}
 */
export function getPOSActionDisplayName(actionType) {
  const names = {
    [POS_ACTION_TYPES.ORDER_CREATE]: 'Create Order',
    [POS_ACTION_TYPES.ORDER_UPDATE]: 'Update Order',
    [POS_ACTION_TYPES.ORDER_ITEM_ADD]: 'Add Item',
    [POS_ACTION_TYPES.ORDER_ITEM_REMOVE]: 'Remove Item',
    [POS_ACTION_TYPES.ORDER_ITEM_MODIFY]: 'Modify Item',
    [POS_ACTION_TYPES.ORDER_DISCOUNT]: 'Apply Discount',
    [POS_ACTION_TYPES.ORDER_VOID]: 'Void Order',
    [POS_ACTION_TYPES.ORDER_CLOSE]: 'Close Order',
    [POS_ACTION_TYPES.PAYMENT_CASH]: 'Cash Payment',
    [POS_ACTION_TYPES.PAYMENT_CARD]: 'Card Payment',
    [POS_ACTION_TYPES.PAYMENT_SPLIT]: 'Split Payment',
    [POS_ACTION_TYPES.PAYMENT_TIP]: 'Add Tip',
    [POS_ACTION_TYPES.TABLE_ASSIGN]: 'Assign Table',
    [POS_ACTION_TYPES.TABLE_TRANSFER]: 'Transfer Table',
    [POS_ACTION_TYPES.TABLE_MERGE]: 'Merge Tables',
    [POS_ACTION_TYPES.SHIFT_OPEN]: 'Open Shift',
    [POS_ACTION_TYPES.SHIFT_CLOSE]: 'Close Shift',
    [POS_ACTION_TYPES.CASH_DROP]: 'Cash Drop',
    [POS_ACTION_TYPES.CASH_PAYOUT]: 'Cash Payout',
    [POS_ACTION_TYPES.PRINT_TICKET]: 'Print Ticket',
    [POS_ACTION_TYPES.KITCHEN_SEND]: 'Send to Kitchen',
    [POS_ACTION_TYPES.KITCHEN_BUMP]: 'Bump Order'
  };
  
  return names[actionType] || actionType;
}

/**
 * Get module name for POS action type
 * @param {string} actionType 
 * @returns {string}
 */
export function getPOSModule(actionType) {
  if (actionType.startsWith('POS_ORDER') || actionType.startsWith('POS_PAYMENT')) {
    return 'Orders';
  }
  if (actionType.startsWith('POS_TABLE')) {
    return 'Tables';
  }
  if (actionType.startsWith('POS_SHIFT') || actionType.startsWith('POS_CASH')) {
    return 'Shifts';
  }
  if (actionType.startsWith('POS_PRINT') || actionType.startsWith('POS_KITCHEN')) {
    return 'Kitchen';
  }
  return 'POS';
}

/**
 * Check if action type is a POS action
 * @param {Object} item - Queue item
 * @returns {boolean}
 */
export function isPOSAction(item) {
  return item.type && item.type.startsWith('POS_');
}

export default POS_POLICIES;
